import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { ThemeLogo } from "@/components/ThemeLogo";
import { ThemeToggle } from "@/components/ThemeToggle";

// Constants
const WHATSAPP_NUMBER = "351939507334";
const WHATSAPP_MESSAGE = encodeURIComponent("Olá Igor! ");

export function Header() {
  const { t, language, setLanguage } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkRole = async (userId: string) => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).limit(1).single();

      setIsAdmin(!!data);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) checkRole(session.user.id);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) checkRole(session.user.id);
      else setIsAdmin(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const scroll = () => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      };

      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(scroll, 100);
      } else {
        scroll();
      }

      setIsOpen(false);
    },
    [location.pathname, navigate],
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const navLinks = [
    { sectionId: "", label: t.nav.home },
    { sectionId: "stock", label: t.nav.stock },
    { sectionId: "servicos", label: t.nav.services },
    { sectionId: "sobre", label: t.nav.about },
    { sectionId: "contacto", label: t.nav.contact },
  ];

  const toggleLanguage = () => {
    setLanguage(language === "pt" ? "en" : "pt");
  };

  const handleNavClick = (sectionId: string) => {
    if (sectionId === "") {
      if (location.pathname !== "/") navigate("/");
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40 transition-opacity duration-300 lg:hidden",
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none",
        )}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent",
          isOpen
            ? "bg-background border-border/10 py-4"
            : scrolled
              ? "glass-header py-3 shadow-sm"
              : "py-4 bg-transparent",
        )}
      >
        <div className="container mx-auto px-4 relative">
          <div className="flex items-center justify-between">
            {/* LEFT SIDE */}
            <div className="flex items-center shrink-0 z-50 relative">
              <Link to="/" className="flex items-center shrink-0">
                <ThemeLogo className="h-10 w-auto md:h-12" />
              </Link>

              {/* Desktop language switcher only */}
              <button
                onClick={toggleLanguage}
                className="hidden lg:flex items-center gap-1.5 ml-8 px-4 py-2 rounded-full bg-secondary/50 hover:bg-secondary transition-colors text-sm font-semibold border border-transparent hover:border-border"
                aria-label="PT | EN – Alterar idioma"
              >
                <span className={cn(language === "pt" ? "text-primary-text" : "text-muted-foreground")}>PT</span>
                <span className="text-muted-foreground/40">|</span>
                <span className={cn(language === "en" ? "text-primary-text" : "text-muted-foreground")}>EN</span>
              </button>
            </div>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {navLinks.map((link) => (
                <button
                  key={link.sectionId}
                  onClick={() => handleNavClick(link.sectionId)}
                  className={cn(
                    "relative text-sm font-medium transition-colors hover:text-primary cursor-pointer",
                    "text-muted-foreground",
                  )}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* RIGHT SIDE DESKTOP ACTIONS */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <ThemeToggle />

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="sm" className="btn-glow gap-2 shadow-primary/20">
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </Button>
              </a>

              {isAdmin && (
                <Link to="/admin">
                  <Button size="sm" variant="outline" className="gap-2">
                    <Shield className="h-4 w-4" />
                    Admin
                  </Button>
                </Link>
              )}
            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden z-50 p-2 rounded-full bg-secondary/80 backdrop-blur-sm hover:bg-secondary transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* MOBILE MENU PANEL */}
          <div
            className={cn(
              "fixed top-0 left-0 right-0 bg-background lg:hidden transition-transform duration-300 ease-in-out flex flex-col z-40 shadow-2xl rounded-b-[2rem] border-b border-border/10",
              isOpen ? "translate-y-0" : "-translate-y-full",
            )}
            style={{
              paddingTop: "80px",
              height: "65vh",
            }}
          >
            <nav className="flex flex-col items-center gap-4 py-6 overflow-y-auto flex-1">
              {/* Theme + Language only inside burger menu */}
              <div className="flex items-center gap-4">
                <ThemeToggle />

                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border/50 text-sm"
                  aria-label="PT / EN – Alterar idioma"
                >
                  <span className={cn("font-bold", language === "pt" ? "text-primary-text" : "text-muted-foreground")}>
                    PT
                  </span>
                  <span className="text-muted-foreground/30">/</span>
                  <span className={cn("font-bold", language === "en" ? "text-primary-text" : "text-muted-foreground")}>
                    EN
                  </span>
                </button>
              </div>

              <div className="w-12 h-1 bg-border rounded-full opacity-50 my-2" />

              {navLinks.map((link, index) => (
                <button
                  key={link.sectionId}
                  onClick={() => handleNavClick(link.sectionId)}
                  className={cn(
                    "text-xl font-semibold tracking-tight transition-all duration-500",
                    "text-foreground/80",
                    isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
                  )}
                  style={{ transitionDelay: `${100 + index * 50}ms` }}
                >
                  {link.label}
                </button>
              ))}

              <div className="w-12 h-1 bg-border rounded-full opacity-50 my-2" />

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2"
              >
                <Button className="btn-glow gap-2 shadow-primary/20">
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </Button>
              </a>

              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  className="text-xl font-semibold tracking-tight text-primary transition-all duration-500"
                >
                  Admin
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}
